import React, { useEffect, useMemo, useState } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { Menu } from 'antd';
import intl from 'react-intl-universal';

import {
  buildAPBillingUri,
  buildAPBillingInventoryUri,
  buildAPResyncOTAUri,
  buildAPUploadExpediaTrxUri,
  buildAPBillingConfigURI,
  buildAPBillingConfigTaxURI,
  buildAPBillingConfigProductCategoryURI,
  buildAPBillingConfigDiscountURI,
  buildAPBillingConfigProductURI,
  buildAPBillingConfigPricingModelURI,
  buildAPBillingInvoiceUri,
  buildAPInvoiceDashboard,
  buildAPHostBilling,
  buildAPMarketplaceUri,
  buildAPQualityDashboardUri,
  buildAPQualityDashboardDemo1Uri,
  buildAPQualityDashboardDemo2Uri,
  buildAPQualityDashboardDemo3Uri,
  buildAPQualityDashboardDemo4Uri,
  buildAPQualityDashboardDemo5Uri,
  buildAPQualityDashboardDemo6Uri,
  buildAPQualityDashboardDemo7Uri,
  buildAPQualityDashboardDemo8Uri
} from 'utils/routes';

import MenuItemTitle from './components/MenuItemTitle/MenuItemTitle';
import { SuperAdminList } from 'utils/constants';

const { Item: MenuItem, SubMenu } = Menu;

const MENU_KEY_LOGOUT = 'logout';

// Helper function to identify which sub-menu a menu key belongs to
const getSubMenuKey = currentMenuKey => {
  if (!currentMenuKey) return undefined;

  const billingUri = buildAPBillingUri();
  const billingConfigURI = buildAPBillingConfigURI();
  const qualityDashboardUri = buildAPQualityDashboardUri();

  if (currentMenuKey.startsWith(billingConfigURI)) {
    return billingConfigURI;
  }
  if (currentMenuKey.startsWith(billingUri)) {
    return billingUri;
  }
  if (currentMenuKey.startsWith(qualityDashboardUri)) {
    return qualityDashboardUri;
  }

  return undefined;
};

// Helper to get the correct menu item key based on the current path
const getMenuItemKey = pathname => {
  // Exact match checking first for specific routes
  const exactPaths = {
    dashboard: buildAPInvoiceDashboard(),
    invoice: buildAPBillingInvoiceUri(),
    host: buildAPHostBilling(),
    marketplace: buildAPMarketplaceUri(),
    inventory: buildAPBillingInventoryUri(),
    pricingModel: buildAPBillingConfigPricingModelURI(),
    productCategory: buildAPBillingConfigProductCategoryURI(),
    product: buildAPBillingConfigProductURI(),
    tax: buildAPBillingConfigTaxURI(),
    discount: buildAPBillingConfigDiscountURI(),
    resyncOTA: buildAPResyncOTAUri(),
    expediaTrx: buildAPUploadExpediaTrxUri(),
    qualityDashboard: buildAPQualityDashboardUri(),
    qualityDashboardDemo1: buildAPQualityDashboardDemo1Uri(),
    qualityDashboardDemo2: buildAPQualityDashboardDemo2Uri(),
    qualityDashboardDemo3: buildAPQualityDashboardDemo3Uri(),
    qualityDashboardDemo4: buildAPQualityDashboardDemo4Uri(),
    qualityDashboardDemo5: buildAPQualityDashboardDemo5Uri(),
    qualityDashboardDemo6: buildAPQualityDashboardDemo6Uri(),
    qualityDashboardDemo7: buildAPQualityDashboardDemo7Uri(),
    qualityDashboardDemo8: buildAPQualityDashboardDemo8Uri()
  };

  // Check for exact path matches first
  for (const [key, path] of Object.entries(exactPaths)) {
    if (pathname === path) {
      return path;
    }
  }

  // Then check for path prefixes (for nested routes)
  for (const [key, path] of Object.entries(exactPaths)) {
    if (pathname.startsWith(path) && path !== '/') {
      return path;
    }
  }

  return pathname;
};

const useGetMenuKeys = location => {
  const [currentMenuKey, setCurrentMenuKey] = useState(undefined);
  const [openKeys, setOpenKeys] = useState([]);

  const currentSelectedKeys = useMemo(() => (!!currentMenuKey ? [currentMenuKey] : []), [currentMenuKey]);

  useEffect(() => {
    const pathname = location.pathname;
    const menuKey = getMenuItemKey(pathname);
    setCurrentMenuKey(menuKey);

    // Determine the parent submenu key
    const subMenuKey = getSubMenuKey(menuKey);

    if (subMenuKey) {
      setOpenKeys(prevOpenKeys => {
        if (!prevOpenKeys.includes(subMenuKey)) {
          return [...prevOpenKeys, subMenuKey]; // Add new submenu key if not already present
        }
        return prevOpenKeys; // Keep existing submenus open
      });
    }
  }, [location.pathname]);

  return { currentMenuKey, currentSelectedKeys, openKeys, setCurrentMenuKey, setOpenKeys };
};

const getUserId = () => {
  const authObj = JSON.parse(localStorage.getItem('auth'));
  return authObj.user._id;
};

const Navbar = ({ history, location }) => {
  const { currentMenuKey, currentSelectedKeys, openKeys, setCurrentMenuKey, setOpenKeys } = useGetMenuKeys(location);

  const handleOnMenuItemClick = menuItem => {
    const selectedMenuKey = menuItem.key;

    if (selectedMenuKey === MENU_KEY_LOGOUT) {
      history.push('/logout');
      return;
    }

    // Set the selected menu key immediately before navigation
    setCurrentMenuKey(selectedMenuKey);
    history.push(selectedMenuKey);
  };

  const handleOnSubMenuClick = newOpenKeys => {
    setOpenKeys(newOpenKeys); // Allow multiple submenus to be open
  };

  return (
    <Menu
      mode="inline"
      selectedKeys={currentSelectedKeys}
      openKeys={openKeys}
      onClick={handleOnMenuItemClick}
      onOpenChange={handleOnSubMenuClick}
      multiple={true} // Ensure that multiple submenus can be open at the same time
      style={{
        height: '100%'
      }}
    >
      <SubMenu key={buildAPBillingUri()} title={<MenuItemTitle iconType="transaction" title="Billing" />}>
        <MenuItem key={buildAPInvoiceDashboard()}>
          <Link to={buildAPInvoiceDashboard()}>
            <MenuItemTitle iconType="inventory" title="Dashboard" />
          </Link>
        </MenuItem>
        <MenuItem key={buildAPBillingInvoiceUri()}>
          <Link to={buildAPBillingInvoiceUri()}>
            <MenuItemTitle iconType="inventory" title="Invoice" />
          </Link>
        </MenuItem>
        <MenuItem key={buildAPHostBilling()}>
          <Link to={buildAPHostBilling()}>
            <MenuItemTitle iconType="inventory" title="Host" />
          </Link>
        </MenuItem>
        <MenuItem key={buildAPBillingInventoryUri()}>
          <Link to={buildAPBillingInventoryUri()}>
            <MenuItemTitle iconType="inventory" title="Inventory" />
          </Link>
        </MenuItem>
      </SubMenu>

      <SubMenu key={buildAPBillingConfigURI()} title={<MenuItemTitle iconType="configuration" title="Billing Configuration" />}>
        <MenuItem key={buildAPBillingConfigPricingModelURI()}>
          <Link to={buildAPBillingConfigPricingModelURI()}>
            <MenuItemTitle iconType="configuration" title="Pricing Model" />
          </Link>
        </MenuItem>
        <MenuItem key={buildAPBillingConfigProductCategoryURI()}>
          <Link to={buildAPBillingConfigProductCategoryURI()}>
            <MenuItemTitle iconType="configuration" title="Product Category" />
          </Link>
        </MenuItem>
        <MenuItem key={buildAPBillingConfigProductURI()}>
          <Link to={buildAPBillingConfigProductURI()}>
            <MenuItemTitle iconType="configuration" title="Product" />
          </Link>
        </MenuItem>
        <MenuItem key={buildAPBillingConfigTaxURI()}>
          <Link to={buildAPBillingConfigTaxURI()}>
            <MenuItemTitle iconType="configuration" title="Tax" />
          </Link>
        </MenuItem>
        <MenuItem key={buildAPBillingConfigDiscountURI()}>
          <Link to={buildAPBillingConfigDiscountURI()}>
            <MenuItemTitle iconType="configuration" title="Discount" />
          </Link>
        </MenuItem>
      </SubMenu>

      <MenuItem key={buildAPMarketplaceUri()}>
        <Link to={buildAPMarketplaceUri()}>
          <MenuItemTitle iconType="marketplace" title="Marketplace" />
        </Link>
      </MenuItem>

      {SuperAdminList.filter(usr => String(usr) === String(getUserId())).length > 0 && (
        <MenuItem key={buildAPResyncOTAUri()}>
          <MenuItemTitle iconType="global" title="Resync OTA" />
        </MenuItem>
      )}

      <MenuItem key={buildAPUploadExpediaTrxUri()}>
        <MenuItemTitle iconType="cloud-upload" title="Expedia Trx" />
      </MenuItem>

      <SubMenu
        key={buildAPQualityDashboardUri()}
        title={<MenuItemTitle iconType="dashboard" title={intl.get('navbar.labels.quality_dashboard').d('Airbnb Quality Dashboard')} />}
      >
        <MenuItem key={buildAPQualityDashboardDemo1Uri()}>
          <MenuItemTitle iconType="dashboard" title="Demo 1 (Airbnb Mock Up)" />
        </MenuItem>
        <MenuItem key={buildAPQualityDashboardDemo2Uri()}>
          <MenuItemTitle iconType="dashboard" title="Demo 2 (Host Tools)" />
        </MenuItem>
        <MenuItem key={buildAPQualityDashboardDemo3Uri()}>
          <MenuItemTitle iconType="dashboard" title="Demo 3 (Hostaway)" />
        </MenuItem>
        <MenuItem key={buildAPQualityDashboardDemo4Uri()}>
          <MenuItemTitle iconType="dashboard" title="Demo 4 (Avantio)" />
        </MenuItem>
        <MenuItem key={buildAPQualityDashboardDemo5Uri()}>
          <MenuItemTitle iconType="dashboard" title="Demo 5 (Hotel Link)" />
        </MenuItem>
        <MenuItem key={buildAPQualityDashboardDemo6Uri()}>
          <MenuItemTitle iconType="dashboard" title="Demo 6 (Uplisting)" />
        </MenuItem>
        <MenuItem key={buildAPQualityDashboardDemo7Uri()}>
          <MenuItemTitle iconType="dashboard" title="Demo 7" />
        </MenuItem>
        <MenuItem key={buildAPQualityDashboardDemo8Uri()}>
          <MenuItemTitle iconType="dashboard" title="Demo 8 (Quality Stats)" />
        </MenuItem>
      </SubMenu>

      <MenuItem key={MENU_KEY_LOGOUT}>
        <MenuItemTitle iconType="logout" title="Logout" />
      </MenuItem>
    </Menu>
  );
};

export default withRouter(Navbar);
